import { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

interface Summary {
  total: number; coreg: number; simplyloan: number;
  unique_leads: number; with_email: number; total_revenue: number;
}
interface CategoryRow {
  category: string | null; conversions: number; unique_leads: number;
  revenue: number; converters: number; applicants: number; rejected: number;
}
interface GeoRow { geo: string | null; conversions: number; unique_leads: number; revenue: number; }
interface IntentRow { intent_level: string; conversions: number; unique_leads: number; revenue: number; }
interface CampaignRow {
  campaign_id: string | null; advertiser_name: string | null; category: string | null;
  geo: string | null; conversions: number; unique_leads: number; revenue: number;
}
interface DailyPoint { date: string; conversions: number; coreg: number; simplyloan: number; }
interface SearchResult {
  lead_id: string; email: string | null; geo: string | null; advertiser_name: string | null;
  category: string | null; campaign_id: string | null; source: string; intent_level: string;
  revenue_dkk_ore: number; conversion_date: string; slug: string | null;
}
interface StatsData {
  summary: Summary; by_category: CategoryRow[]; by_geo: GeoRow[];
  by_intent: IntentRow[]; top_campaigns: CampaignRow[]; daily: DailyPoint[];
}

const CATEGORY_COLORS: Record<string, string> = {
  Loan: '#3b82f6', Sweepstake: '#f59e0b', MicroLoan: '#8b5cf6', Creditcard: '#ec4899',
  Insurance: '#10b981', Nutra: '#ef4444', Energy: '#06b6d4', Finance: '#6366f1', Other: '#9ca3af',
};

function fmt(n: number) { return n.toLocaleString(); }
function fmtDKK(ore: number) { return `${(ore / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} DKK`; }

function KpiCard({ label, value, sub, icon }: { label: string; value: string | number; sub?: string; icon: string }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <p className="text-sm font-medium text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? fmt(value) : value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function CategoryDonut({ data }: { data: CategoryRow[] }) {
  const filtered = data.filter(d => d.category);
  const total = filtered.reduce((s, d) => s + d.conversions, 0) || 1;
  let cumulative = 0;
  const parts = filtered.map(d => {
    const pct = (d.conversions / total) * 100;
    const start = cumulative;
    cumulative += pct;
    return { ...d, pct, start, color: CATEGORY_COLORS[d.category!] || '#9ca3af' };
  });
  const gradient = parts.map(p => `${p.color} ${p.start}% ${p.start + p.pct}%`).join(', ');

  return (
    <div className="flex items-center gap-6">
      <div className="w-36 h-36 rounded-full flex-shrink-0" style={{ background: `conic-gradient(${gradient})` }}>
        <div className="w-full h-full rounded-full flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-inner">
            <span className="text-sm font-bold text-gray-700">{fmt(total)}</span>
          </div>
        </div>
      </div>
      <div className="space-y-1.5 flex-1">
        {parts.map(p => (
          <div key={p.category} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-gray-700">{p.category}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-xs">{p.pct.toFixed(1)}%</span>
              <span className="text-gray-600 font-medium w-14 text-right">{fmt(p.conversions)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyChart({ data }: { data: DailyPoint[] }) {
  if (!data.length) return <p className="text-gray-400 text-sm">No daily data</p>;
  const max = Math.max(...data.map(d => d.conversions), 1);
  return (
    <div className="flex items-end gap-[2px] h-32">
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center flex-1 group relative">
          <div className="w-full flex flex-col-reverse">
            <div className="w-full bg-indigo-400 rounded-t-sm" style={{ height: `${(d.coreg / max) * 120}px` }}
              title={`${d.date}: ${d.coreg} coreg`} />
            <div className="w-full bg-orange-400 rounded-t-sm" style={{ height: `${(d.simplyloan / max) * 120}px` }}
              title={`${d.date}: ${d.simplyloan} simplyloan`} />
          </div>
          {i % 7 === 0 && <span className="text-[7px] text-gray-400 mt-1 whitespace-nowrap">{d.date.slice(5)}</span>}
          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
            {d.date}: {d.conversions} ({d.coreg} coreg, {d.simplyloan} SL)
          </div>
        </div>
      ))}
    </div>
  );
}

function IntentFunnel({ data }: { data: IntentRow[] }) {
  const order = ['converter', 'applicant', 'rejected'];
  const colors = { converter: '#22c55e', applicant: '#eab308', rejected: '#ef4444' };
  const labels = { converter: 'Converters', applicant: 'Applicants (no rev)', rejected: 'Rejected' };
  const sorted = order.map(o => data.find(d => d.intent_level === o)).filter(Boolean) as IntentRow[];
  const maxConv = Math.max(...sorted.map(d => d.conversions), 1);

  return (
    <div className="space-y-3">
      {sorted.map(d => (
        <div key={d.intent_level}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-700">
              {labels[d.intent_level as keyof typeof labels] || d.intent_level}
            </span>
            <span className="text-sm text-gray-500">{fmt(d.conversions)} ({fmt(d.unique_leads)} unique)</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(d.conversions / maxConv) * 100}%`,
                backgroundColor: colors[d.intent_level as keyof typeof colors] || '#9ca3af' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ConverterIntelligence() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geo, setGeo] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [days, setDays] = useState('90');
  const [syncing, setSyncing] = useState(false);
  // Search
  const [searchTab, setSearchTab] = useState<'stats' | 'search'>('stats');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCat, setSearchCat] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searching, setSearching] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (geo) params.set('geo', geo);
      if (category) params.set('category', category);
      if (source) params.set('source', source);
      if (days) params.set('days', days);
      const resp = await fetch(`${API_BASE_URL}/api/converters/stats?${params}`);
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const json = await resp.json();
      setStats(json.data);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [geo, category, source, days]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const doSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) {
        if (searchQuery.includes('@')) params.set('email', searchQuery);
        else params.set('lead_id', searchQuery);
      }
      if (searchCat) params.set('category', searchCat);
      if (geo) params.set('geo', geo);
      params.set('limit', '50');
      const resp = await fetch(`${API_BASE_URL}/api/converters/search?${params}`);
      if (!resp.ok) throw new Error(`API ${resp.status}`);
      const json = await resp.json();
      setSearchResults(json.data.results || []);
      setSearchTotal(json.data.total || 0);
    } catch (e: any) { setError(e.message); }
    finally { setSearching(false); }
  };

  const doSync = async () => {
    setSyncing(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/converters/sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_resync: false }),
      });
      if (!resp.ok) throw new Error(`Sync failed: ${resp.status}`);
      await fetchStats();
    } catch (e: any) { setError(e.message); }
    finally { setSyncing(false); }
  };

  const s = stats?.summary;

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🎯 Converter Intelligence</h1>
            <p className="text-gray-500 text-sm mt-1">Subscriber conversion tracking, categories & intent analysis</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Tabs */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => setSearchTab('stats')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${searchTab === 'stats' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                📊 Stats
              </button>
              <button onClick={() => setSearchTab('search')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${searchTab === 'search' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                🔍 Search
              </button>
            </div>
            <button onClick={doSync} disabled={syncing}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {syncing ? '⏳ Syncing...' : '🔄 Sync'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <select value={geo} onChange={e => setGeo(e.target.value)}
            className="px-3 py-1.5 border rounded-lg text-sm bg-white">
            <option value="">All Geos</option>
            {['SE', 'NO', 'FI', 'DE'].map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="px-3 py-1.5 border rounded-lg text-sm bg-white">
            <option value="">All Categories</option>
            {['Loan', 'Sweepstake', 'MicroLoan', 'Creditcard', 'Insurance', 'Nutra', 'Energy', 'Finance', 'Other'].map(c =>
              <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={source} onChange={e => setSource(e.target.value)}
            className="px-3 py-1.5 border rounded-lg text-sm bg-white">
            <option value="">All Sources</option>
            <option value="coreg">CoReg</option>
            <option value="simplyloan">SimplyLoan</option>
          </select>
          <select value={days} onChange={e => setDays(e.target.value)}
            className="px-3 py-1.5 border rounded-lg text-sm bg-white">
            {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>Last {d}d</option>)}
          </select>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4"><strong>Error:</strong> {error}</div>}

        {/* ===== STATS TAB ===== */}
        {searchTab === 'stats' && stats && !loading && (
          <div className="space-y-6">
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <KpiCard icon="📊" label="Total Conversions" value={s?.total || 0} />
              <KpiCard icon="🎯" label="CoReg" value={s?.coreg || 0}
                sub={s ? `${((s.coreg / (s.total || 1)) * 100).toFixed(1)}% of total` : ''} />
              <KpiCard icon="🏦" label="SimplyLoan" value={s?.simplyloan || 0} />
              <KpiCard icon="👤" label="Unique Leads" value={s?.unique_leads || 0}
                sub={s ? `${fmt(s.with_email)} with email` : ''} />
              <KpiCard icon="💰" label="Revenue" value={fmtDKK(s?.total_revenue || 0)} />
            </div>

            {/* Category + Intent row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">📁 By Category</h2>
                <CategoryDonut data={stats.by_category} />
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">🎯 Intent Funnel</h2>
                <IntentFunnel data={stats.by_intent} />
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {stats.by_geo.filter(g => g.geo).map(g => (
                    <div key={g.geo} className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-gray-900">{fmt(g.conversions)}</p>
                      <p className="text-xs text-gray-500">{g.geo} · {fmt(g.unique_leads)} leads</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Daily chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">📈 Daily Conversions</h2>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400" /> CoReg</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400" /> SimplyLoan</span>
                </div>
              </div>
              <DailyChart data={stats.daily} />
            </div>

            {/* Top campaigns table */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🏆 Top Campaigns</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 border-b">
                      <th className="text-left py-2 font-medium">Campaign</th>
                      <th className="text-left py-2 font-medium">Advertiser</th>
                      <th className="text-left py-2 font-medium">Category</th>
                      <th className="text-left py-2 font-medium">Geo</th>
                      <th className="text-right py-2 font-medium">Conv</th>
                      <th className="text-right py-2 font-medium">Leads</th>
                      <th className="text-right py-2 font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_campaigns.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 text-gray-700 font-mono text-xs">{c.campaign_id || '—'}</td>
                        <td className="py-2 text-gray-700">{c.advertiser_name || '—'}</td>
                        <td className="py-2">
                          {c.category ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ backgroundColor: `${CATEGORY_COLORS[c.category] || '#9ca3af'}20`,
                                color: CATEGORY_COLORS[c.category] || '#6b7280' }}>
                              {c.category}
                            </span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2 text-gray-600">{c.geo || '—'}</td>
                        <td className="py-2 text-right font-medium">{fmt(c.conversions)}</td>
                        <td className="py-2 text-right text-gray-500">{fmt(c.unique_leads)}</td>
                        <td className="py-2 text-right text-green-600 font-medium">{fmtDKK(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Category detail table */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 Category Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 border-b">
                      <th className="text-left py-2 font-medium">Category</th>
                      <th className="text-right py-2 font-medium">Conversions</th>
                      <th className="text-right py-2 font-medium">Leads</th>
                      <th className="text-right py-2 font-medium">Converters</th>
                      <th className="text-right py-2 font-medium">Applicants</th>
                      <th className="text-right py-2 font-medium">Rejected</th>
                      <th className="text-right py-2 font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_category.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
                        onClick={() => { setCategory(c.category || ''); }}>
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: CATEGORY_COLORS[c.category || ''] || '#9ca3af' }} />
                            <span className="text-gray-700 font-medium">{c.category || 'Uncategorized'}</span>
                          </div>
                        </td>
                        <td className="py-2 text-right font-medium">{fmt(c.conversions)}</td>
                        <td className="py-2 text-right text-gray-500">{fmt(c.unique_leads)}</td>
                        <td className="py-2 text-right text-green-600">{fmt(c.converters)}</td>
                        <td className="py-2 text-right text-yellow-600">{fmt(c.applicants)}</td>
                        <td className="py-2 text-right text-red-600">{fmt(c.rejected)}</td>
                        <td className="py-2 text-right text-green-600 font-medium">{fmtDKK(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ===== SEARCH TAB ===== */}
        {searchTab === 'search' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🔍 Search Converters</h2>
              <div className="flex gap-3 mb-4">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Lead ID or email..." onKeyDown={e => e.key === 'Enter' && doSearch()}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm" />
                <select value={searchCat} onChange={e => setSearchCat(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-white">
                  <option value="">All Categories</option>
                  {['Loan', 'Sweepstake', 'MicroLoan', 'Creditcard', 'Insurance', 'Nutra'].map(c =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={doSearch} disabled={searching}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>

              {searchResults.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-3">{fmt(searchTotal)} results</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 border-b text-xs">
                          <th className="text-left py-2 font-medium">Lead</th>
                          <th className="text-left py-2 font-medium">Email</th>
                          <th className="text-left py-2 font-medium">Geo</th>
                          <th className="text-left py-2 font-medium">Advertiser</th>
                          <th className="text-left py-2 font-medium">Category</th>
                          <th className="text-left py-2 font-medium">Source</th>
                          <th className="text-left py-2 font-medium">Intent</th>
                          <th className="text-right py-2 font-medium">Revenue</th>
                          <th className="text-left py-2 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((r, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2 font-mono text-xs text-gray-700">{r.lead_id}</td>
                            <td className="py-2 text-gray-600 text-xs">{r.email || '—'}</td>
                            <td className="py-2 text-gray-600">{r.geo || '—'}</td>
                            <td className="py-2 text-gray-700">{r.advertiser_name || '—'}</td>
                            <td className="py-2">
                              {r.category ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                                  style={{ backgroundColor: `${CATEGORY_COLORS[r.category] || '#9ca3af'}20`,
                                    color: CATEGORY_COLORS[r.category] || '#6b7280' }}>
                                  {r.category}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.source === 'simplyloan' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                {r.source}
                              </span>
                            </td>
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.intent_level === 'converter' ? 'bg-green-100 text-green-700' : r.intent_level === 'applicant' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                {r.intent_level}
                              </span>
                            </td>
                            <td className="py-2 text-right text-green-600 text-xs">{fmtDKK(r.revenue_dkk_ore)}</td>
                            <td className="py-2 text-gray-500 text-xs">{r.conversion_date?.slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {searchResults.length === 0 && !searching && searchQuery && (
                <p className="text-gray-400 text-sm text-center py-8">No results. Try a different query.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
